import { ReadonlyVec3, ReadonlyVec4, mat4, vec2, vec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix';
import { UniformSampler } from '../../../core/utils/uniforms/UniformSampler';
import { UniformVec2 } from '../../../core/utils/uniforms/UniformVec2';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { ImageObject } from '../../image/ImageObject';
import { ContourTexture } from '../../structure/ContourTexture';
import { Grid } from '../Grid';
import { CustomTargetObject } from './CustomTargetObject';

/**
 * {@link CustomTargetObject} that adapts to the data of a {@link ImageObject} by generating an adapted sphere around a given center point.\
 * The sphere is generated using a flood fill algorithm, which is implemented in the {@link FloodFillShader}.\
 * The {@link FloodFillShader} is used to generate a {@link SignedDistanceField} that represents the sphere adapted to target {@link ImageObject}'s contents.
 */
export class AdaptiveSphere extends CustomTargetObject {
    /**
     * The amount of padding added to each side of the contour data in millimeters.\
     * This is useful to support better visualization as it provides smoother contours.
     */
    protected margin: number;

    /**
     * Diameter of the sphere in millimeters.
     */
    protected diameter: number;

    /**
     * {@link FrameBufferObject} used to {@link updateTexture update the texture} of this {@link AdaptiveSphere}.
     */
    protected fbo?: FrameBufferObject;

    /**
     * {@link FloodFillShader} used to render the adaptive sphere sdf data on the SDF texture.
     */
    protected readonly shader: FloodFillShader;

    /**
     * {@link ImageObject} the data of which the {@link AdaptiveSphere} should adapt to.
     */
    protected readonly image: ImageObject;

    /**
     * Value between 0 and 1 that determines how similar the neighboring pixels have to be to be considered part of the target volume.\
     * This is used by the {@link FloodFillShader} to determine if the neighboring pixels are similar enough to be considered part of the target volume.
     */
    private _similarityThreshold = 0.2;

    /**
     * {@link AdaptiveSphere} constructor.\
     * @param target {@link ImageObject} that this {@link AdaptiveSphere} should adapt to.
     * @param center {@link ReadonlyVec3 PatientSpace coordinates} of the center of the {@link AdaptiveSphere}.
     * @param diameter Diameter of the {@link AdaptiveSphere} in millimeters.
     * @param resolution Resolution of the {@link AdaptiveSphere}'s SDF texture in millimeters/pixel.
     * @param color Color of the {@link AdaptiveSphere}'s outline.
     * @param margin Amount of padding added to each side of the contour data in millimeters.
     */
    public constructor(
        target: ImageObject,
        center: ReadonlyVec3,
        diameter: number,
        resolution: number,
        color: ReadonlyVec4,
        margin: number = ContourTexture.DEFAULT_MARGIN
    ) {
        super(target);
        this.diameter = diameter;
        this.margin = margin;
        this._color = [color[0], color[1], color[2]];
        this._contourThickness = 0.5;
        this.renderOptions = { fillAlpha: 0.25 };
        this.shader = new FloodFillShader();
        this.image = target;

        const sizeInPixels = this.computeSizeInPixels(resolution);
        const resZ = this.image.parent.grid.resZ;
        const corner = this.computeCorner(
            center,
            sizeInPixels,
            resolution,
            resZ
        );
        this._contourTexture = new ContourTexture(
            this._imageGrid,
            sizeInPixels,
            corner,
            this.margin,
            vec3.fromValues(resolution, resolution, resZ)
        );
        this.updateTexture();
    }

    /**
     * Method to {@link SignedDistanceField.bind bind} the ContourTexture's SDF.
     * @returns True to indicate that the {@link SignedDistanceField} was successfully bound.
     */
    public bindReadTexture(): boolean {
        this._contourTexture!.signedDistanceField.bind();
        return true;
    }

    /**
     * Computes the planar size in pixels based on diameter, margin,
     * and resolution. Z is always 1 because the adaptive brush is 2D
     * (single-slice). The planar size is rounded up and made odd so
     * the flood fill seed lands on the centre voxel.
     * @param resolution The planar resolution in mm/px.
     */
    private computeSizeInPixels(resolution: number): vec3 {
        let planarSize = Math.ceil(this.brushSizeMm / resolution);
        // Ensure odd so there is a single centre pixel
        planarSize = planarSize & 1 ? planarSize : planarSize + 1;
        return vec3.fromValues(planarSize, planarSize, 1);
    }

    /**
     * Computes the world-space corner position from a center point and size.
     */
    private computeCorner(
        center: ReadonlyVec3,
        sizeInPixels: ReadonlyVec3,
        resolution: number,
        resZ: number
    ): vec3 {
        const orientation = this.image.parent.grid.getOrientation();

        const halfSizeLocal = vec3.fromValues(
            (sizeInPixels[0] * resolution) / 2,
            (sizeInPixels[1] * resolution) / 2,
            (sizeInPixels[2] * resZ) / 2
        );
        const halfSizePatient = vec3.transformMat3(
            vec3.create(),
            halfSizeLocal,
            orientation
        );
        return vec3.subtract(vec3.create(), center, halfSizePatient);
    }

    public get radius(): number {
        return this.diameter * 0.5;
    }

    public get brushSizeMm(): number {
        return this.diameter + 2 * this.margin;
    }

    public get grid(): Grid {
        return this._contourTexture!.textureGrid;
    }

    /**
     * Method to update the {@link diameter} of the {@link AdaptiveSphere}.
     * @param size The new {@link diameter} in millimeters.
     */
    public resize(size: number): void {
        const currentCenter = vec3.clone(this.grid.center);
        this.diameter = size;
        const resolution = this.grid.resX;
        const resZ = this.image.parent.grid.resZ;
        const sizeInPixels = this.computeSizeInPixels(resolution);
        const corner = this.computeCorner(
            currentCenter,
            sizeInPixels,
            resolution,
            resZ
        );
        this._contourTexture!.updateGrid(sizeInPixels, corner);
        this.updateTexture();
    }

    public setPosition(position: ReadonlyVec3): void {
        const grid = this.grid;
        const sizeInPixels = grid.getSize();
        const corner = this.computeCorner(
            position,
            sizeInPixels,
            grid.resX,
            this.image.parent.grid.resZ
        );
        this._contourTexture!.updateGrid(sizeInPixels, corner);
        this.updateTexture();
    }

    /**
     * Method to update the SDF of this {@link AdaptiveSphere}.\
     * Uses ping-pong flood fill between two temporary work buffers.
     * After completion the result is copied into the ContourTexture's
     * SDF and both work buffers are deleted.
     */
    protected updateTexture(): void {
        const grid = this.grid;
        if (grid.isEmpty) {
            throw new Error(
                `Attempt to update the texture of AdaptiveSphere with an empty Grid`
            );
        }
        const sizeInPixels = grid.getSize();
        const planarSize = vec2.fromValues(sizeInPixels[0], sizeInPixels[1]);

        const primarySdf = this._contourTexture!.signedDistanceField;
        const workBuffer = new SignedDistanceField(sizeInPixels);

        if (this.fbo) {
            if (!vec2.equals(this.fbo.size, planarSize)) {
                this.fbo.resize(planarSize);
            }
        } else {
            this.fbo = new FrameBufferObject(planarSize);
        }
        this.fbo.bind();
        gl.clearColor(0, 0, 0, 1);
        gl.viewport(0, 0, planarSize[0], planarSize[1]);

        gl.activeTexture(gl.TEXTURE1);
        this.image.bindTexture();
        gl.activeTexture(gl.TEXTURE0);

        const vao = RenderStorage.getQuadVAO();
        vao.bind(0);

        this.shader.start();
        this.shader.voxelStep.loadVec3(
            vec3.divide(vec3.create(), vec3.fromValues(1, 1, 1), sizeInPixels)
        );

        const patientSpaceCenter = grid.center;
        const mouseImageCoords =
            this.image.grid.worldToTextureUVPosition(patientSpaceCenter);

        this.shader.mouseImageCoords.loadVec3(mouseImageCoords);
        this.shader.similarityThreshold.loadFloat(this._similarityThreshold);
        if (this.image.windowLevel) {
            this.shader.windowLevel.loadVec2(this.image.windowLevel.asVec2);
        }
        this.shader.radius.loadFloat(this.diameter * 0.5);
        this.shader.margin.loadFloat(this.margin);

        // Maps sphere grid clip space [-1,1]^3 to image texture UV [0,1]^3
        // via world space: clipToImageUV = worldToTextureUV * clipToWorld
        const clipToWorld = mat4.invert(mat4.create(), grid.worldToClipSpace);
        const toImageTextureUV = mat4.multiply(
            mat4.create(),
            this.image.grid.worldToTextureUV,
            clipToWorld
        );
        this.shader.toImageTextureCoords.loadMatrix(toImageTextureUV);

        // Depth is 1 so k=0 (centre of the single slice)
        this.shader.k.loadFloat(0.0);

        const iterations = Math.ceil(
            Math.max(sizeInPixels[0], sizeInPixels[1]) * 0.5
        );

        // Ping-pong between the ContourTexture's SDF and a
        // temporary work buffer.
        const buffers = [primarySdf, workBuffer];

        for (let i = 0; i < iterations; ++i) {
            const read = buffers[~i & 1];
            read.bind();
            const write = buffers[i & 1];
            this.fbo.createTextureLayer(write.texture, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }

        this.shader.stop();
        vao.unbind(0);
        this.fbo.unbind();

        // If the result ended up in the work buffer, copy it
        // into the ContourTexture's SDF.
        const lastWriteIndex = (iterations - 1) & 1;
        if (lastWriteIndex === 1) {
            this._contourTexture!.resetSdfData(workBuffer.pixels);
        }

        workBuffer.delete();
    }

    /**
     * @Override
     * Method to delete this {@link CustomTargetObject}.\
     * This calls the {@link CustomTargetObject.delete delete} method of the {@link CustomTargetObject} superclass, which deletes the ContourTexture (and its SDF).\
     * But it also deletes the work buffer, {@link fbo} and shaders.
     * @returns True to indicate that the {@link CustomTargetObject} was successfully deleted.
     */
    public delete(): boolean {
        super.delete();
        this.shader.delete();
        if (this.fbo) {
            this.fbo.delete();
        }
        return true;
    }
}

class FloodFillShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;

    in vec2 position;

    out vec3 brushTextureCoords;
    out vec3 imageCoords;

    out float imageValue;

    uniform sampler3D imageTexture;

    uniform float k; // value in between -1 and 1
    uniform mat4 toImageTextureCoords;

    uniform vec2 windowLevel;

    uniform vec3 mouseImageCoords;

    float valueModification(float value){
        value = (value - windowLevel.y + (windowLevel.x * 0.5)) / windowLevel.x;
        return clamp(value, 0.0, 1.0);
    }

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        brushTextureCoords = vec3(position, k);
        imageCoords = (toImageTextureCoords * vec4(position.x, position.y, k, 1.0)).xyz;
        imageValue = valueModification(texture(imageTexture, mouseImageCoords).r);
    }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;

    in vec3 brushTextureCoords;
    in vec3 imageCoords;

    in float imageValue;

    out float value;

    uniform sampler3D brushTexture;
    uniform sampler3D imageTexture;
    uniform vec3 voxelStep; // 1 / sizeInPixels, one voxel step in texture UV [0,1]
    uniform float similarityThreshold;
    uniform float radius;
    uniform float margin;
    uniform vec2 windowLevel;

    float valueModification(float value){
        value = (value - windowLevel.y + (windowLevel.x * 0.5)) / windowLevel.x;
        return clamp(value, 0.0, 1.0);
    }

    void main() {
        if(imageCoords.x < 0.0 || imageCoords.x > 1.0 || imageCoords.y < 0.0 || imageCoords.y > 1.0 || imageCoords.z < 0.0 || imageCoords.z > 1.0) {
            discard;
        }

        // in [0, 1] range, with (0.5, 0.5, 0.5) in the center of the texture
        vec3 textureCoords = (brushTextureCoords + vec3(1.0)) * 0.5;
        vec3 fromCenter = abs(textureCoords - vec3(0.5));

        if(fromCenter.x < voxelStep.x && fromCenter.y < voxelStep.y && fromCenter.z < voxelStep.z) {
            // Seed the center voxel to start the flood fill
            value = 1.0;
            return;
        }

        float totalRadius = radius + margin;
        float distFromCenterMm = length(brushTextureCoords * totalRadius);
        // Cap limits SDF to the sphere shape: 0.5 at surface, falls to 0 at margin edge
        float cap = 0.5 - 0.5 * (distFromCenterMm - radius) / margin;

        value = texture(brushTexture, textureCoords).r;
        bool filled = value >= 0.5;
        if(filled) { value = 1.0; }

        bool hasFilledNeighbor = false;
        int filledCount = 0;
        int neighborCount = 0;

        // 2D flood fill: only check in-plane (XY) neighbors
        for(float x = -1.0; x <= 1.0; ++x){
            for(float y = -1.0; y <= 1.0; ++y){
                if(x == 0.0 && y == 0.0) { continue; }

                vec3 delta = vec3(x, y, 0.0) * voxelStep;
                vec3 neighborCoords = textureCoords + delta;
                if(neighborCoords.x < 0.0 || neighborCoords.x > 1.0 || neighborCoords.y < 0.0 || neighborCoords.y > 1.0) {
                    continue;
                }

                neighborCount++;
                float ref = texture(brushTexture, neighborCoords).r;
                float stepDist = length(delta * totalRadius) / margin;

                if(!filled) {
                    if(ref > 0.5) {
                        hasFilledNeighbor = true;
                        filledCount++;
                    } else {
                        value = max(ref - stepDist, value);
                    }
                } else {
                    // Smoothing: maintain SDF distance near boundaries
                    value = min(ref < 0.5 ? 0.5 + stepDist : ref + stepDist, value);
                }
            }
        }

        // Only fill if connected to existing structure AND image is similar
        if(!filled && hasFilledNeighbor) {
            float refImageValue = valueModification(texture(imageTexture, imageCoords).r);
            float deltaImage = abs(imageValue - refImageValue) / similarityThreshold;
            float similarity = 1.0 - deltaImage;
            // Neighbor density: more filled neighbors makes filling easier
            // Fills small holes, reduces narrow bridges
            float neighborSupport = float(filledCount) / max(float(neighborCount), 1.0);
            float fillStrength = mix(similarity, 1.0, neighborSupport * neighborSupport);
            value = max(0.5 + 0.5 * fillStrength, value);
        }

        value = min(value, cap);
    }
    `;

    public readonly toImageTextureCoords: UniformMatrix;

    public readonly k: UniformFloat;

    private readonly brushTexture: UniformSampler;

    private readonly imageTexture: UniformSampler;

    /** One voxel step in texture UV [0,1] space (1 / sizeInPixels). */
    public readonly voxelStep: UniformVec3;

    public readonly mouseImageCoords: UniformVec3;

    public readonly radius: UniformFloat;
    public readonly margin: UniformFloat;

    /**
     * Threshold value that determines if the neighboring pixels are similar enough to be considered part of the target volume.\
     * This should be a value in between 0 and 1.
     */
    public readonly similarityThreshold: UniformFloat;

    public readonly windowLevel: UniformVec2;

    public constructor() {
        super(
            FloodFillShader.VERTEX_CONTENT,
            FloodFillShader.FRAGMENT_CONTENT,
            'position'
        );
        this.toImageTextureCoords = new UniformMatrix('toImageTextureCoords');
        this.k = new UniformFloat('k');
        this.brushTexture = new UniformSampler('brushTexture');
        this.imageTexture = new UniformSampler('imageTexture');
        this.radius = new UniformFloat('radius');
        this.margin = new UniformFloat('margin');
        this.voxelStep = new UniformVec3('voxelStep');
        this.mouseImageCoords = new UniformVec3('mouseImageCoords');
        this.similarityThreshold = new UniformFloat('similarityThreshold');
        this.windowLevel = new UniformVec2('windowLevel');
        super.storeUniformLocations(
            this.toImageTextureCoords,
            this.k,
            this.brushTexture,
            this.imageTexture,
            this.voxelStep,
            this.mouseImageCoords,
            this.radius,
            this.margin,
            this.similarityThreshold,
            this.windowLevel
        );
        super.start();
        this.brushTexture.loadTextureUnit(0);
        this.imageTexture.loadTextureUnit(1);
        super.stop();
    }
}
